import { useIntl } from '@capsicum/common';
import { Button, TextLineOrSkeleton, useRouter } from '@capsicum/common-web';
import { CopyClipboardIcon } from '@capsicum/common-web/icons';
import styled from 'styled-components';

export type AddressCopyCardProps = {
	isLoading?: boolean;
	network: {
		address?: string;
		label?: string;
		shortCode?: string;
	};
	showSnack: (show: boolean) => void;
};

export const AddressCopyCard = ({ isLoading, network, showSnack }: AddressCopyCardProps) => {
	const { push } = useRouter();
	const { $t } = useIntl();

	const texts = {
		backButton: $t('wallet.receive.address.walletButton'),
		waitForAddress: $t('wallet.receive.address.waitForAddress')
	};

	const askUserToWaitForAddress = !isLoading && !network.address;
	const noAddress = askUserToWaitForAddress || isLoading;

	return (
		<S.Container>
			{!askUserToWaitForAddress && <S.TextInfo>{`${network.label} (${network.shortCode}) address:`}</S.TextInfo>}
			<S.StyledText>
				<S.StyledTextOrSkeleton isLoading={isLoading}>{network.address}</S.StyledTextOrSkeleton>
				{askUserToWaitForAddress && <S.WaitForAddress>{texts.waitForAddress}</S.WaitForAddress>}
			</S.StyledText>
			<Button
				color="brand"
				disabled={noAddress}
				iconEnd={{ icon: { src: CopyClipboardIcon } }}
				isLoading={noAddress}
				testID={`receive-${network.label}-copyClipboardButton`}
				text={$t('wallet.receive.address.copy')}
				variant="default"
				onClick={() => {
					showSnack(true);
					navigator.clipboard.writeText(network.address || '');
				}}
			/>
			<Button
				color="neutral"
				testID={`receive-${network.label}-backButton`}
				text={texts.backButton}
				variant="text"
				onClick={() => push('/wallet')}
			/>
		</S.Container>
	);
};
namespace S {
	export const Container = styled.div`
		display: flex;
		flex-direction: column;
		align-items: center;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		gap: ${({ theme }) => theme.spacings.xsmall};
		border-radius: ${({ theme }) => theme.spacings.xxsmall};
		padding: ${({ theme }) => `${theme.spacings.xlarge} ${theme.spacings.small}`};
		width: 100%;
	`;
	export const TextInfo = styled.div`
		align-items: center;
		text-align: center;
		display: flex;
		${({ theme }) => theme.fonts.text.text3}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;
	export const StyledText = styled.div`
		color: ${({ theme }) => theme.colors.text.brand.enabled};
		${({ theme }) => theme.fonts.text.text8}
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			${({ theme }) => theme.fonts.text.text4}
		}
		text-align: center;
	`;

	export const WaitForAddress = styled.div`
		width: 24em;
	`;

	export const StyledTextOrSkeleton = styled(TextLineOrSkeleton)`
		height: 1lh;
		width: 24em;
	`;
}
